/**
 * CutsceneSystem — runs scripted sequences of camera movements,
 * dialogue, animations, and callbacks.
 *
 * Ported from the Phaser codebase. Uses Babylon.js GUI for dialogue
 * and the existing CameraSystem for panning/follow control.
 *
 * Used for Elder encounters, level intros/outros, and story beats.
 * Disables player input during a cutscene via the EventBus.
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Animation } from "@babylonjs/core/Animations/animation";
import { CubicEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { CameraSystem } from "./CameraSystem";
import { AccessibilitySystem } from "./AccessibilitySystem";
import { ElderRevealPostProcess, ElderRevealPreset } from "./ElderRevealPostProcess";
import { resolveDialogueAdvance } from "./dialogueAdvance";
import { resolveContrastStyle } from "../ui/menuFonts";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { COLORS } from "../config/GameConfig";
import { configureResponsiveFullscreenUI } from "../ui/guiScaling";
import { applyWrappedTextLayout } from "../ui/wrappedText";
import { formatControlText, isAnyGamepadButtonPressed } from "../ui/ControlGlyphs";

export type CutsceneStepType = 'pan' | 'dialogue' | 'wait' | 'fade' | 'callback' | 'shake' | 'reveal';

/**
 * Visual presentation style for a dialogue step.
 * - `standard`: named-speaker pill at the bottom of the screen (default).
 * - `vision`: centered italic whisper with no speaker pill, painted over
 *   an ink-bloom backdrop. Used by the Making Biscuits vision cutscenes
 *   (see `systems/VisionRenderer.ts`).
 */
export type DialogueStyle = 'standard' | 'vision';

export interface CutsceneStep {
  type: CutsceneStepType;
  // Pan
  target?: { x: number; y: number };
  duration?: number;
  ease?: string;
  // Dialogue
  speaker?: string;
  text?: string;
  /**
   * Optional style flag for `type: 'dialogue'` steps. Defaults to
   * `'standard'` so legacy cutscenes render identically.
   */
  style?: DialogueStyle;
  /** Local cinematic composition; ordinary dialogue retains its bottom band. */
  dialoguePlacement?: 'top' | 'bottom';
  // Fade
  fadeDirection?: 'in' | 'out';
  color?: { r: number; g: number; b: number };
  // Callback
  fn?: () => void;
  // Shake
  intensity?: number;
  // Reveal (Elder reveal — Milestone 4.C). `preset` selects between
  // canon beats: starry-void (Kitty, book p.14) and parlor-dissolve
  // (Gus, book p.19). `duration` reuses `durationMs`-equivalent
  // semantics (ms) so the field shape matches other timed steps.
  preset?: ElderRevealPreset;
}

export class CutsceneSystem {
  private scene: Scene;
  private cameraSystem: CameraSystem;
  private steps: CutsceneStep[] = [];
  private currentStep = 0;
  private isPlaying = false;

  // Dialogue GUI
  private guiTexture: AdvancedDynamicTexture | null = null;
  private disposeGuiScaling: (() => void) | null = null;
  private dialogueContainer: Rectangle | null = null;
  private speakerTextBlock: TextBlock | null = null;
  private dialogueTextBlock: TextBlock | null = null;
  private dialoguePlacement: 'top' | 'bottom' = 'bottom';

  // Vision-style dialogue GUI — centered italic whisper with ink-bloom
  // backdrop. Created lazily on first use and reused for the duration of
  // the vision sequence (disposed via `dispose()` alongside the main GUI).
  private visionContainer: Rectangle | null = null;
  private visionTextBlock: TextBlock | null = null;
  private lastDialogueStyle: DialogueStyle = 'standard';

  // Fade overlay
  private fadeOverlay: Rectangle | null = null;

  // Pending timers so we can clean up on stop()
  private pendingTimers: ReturnType<typeof setTimeout>[] = [];
  /**
   * Invalidated by play() and stop(). Every asynchronous step continuation
   * (timers, animation-end callbacks, reveal completion) captures it via
   * next(), so a continuation that fires after a skip/stop — e.g. the pan
   * animation's end callback, which stopAnimation() itself triggers — can
   * no longer restart the step chain over live gameplay.
   */
  private runToken = 0;

  // A single pending `playDeferred` handle. Scenes schedule their intro
  // cutscene via `playDeferred(steps, 600)` so that `__startAtPuzzle` can
  // abort it via `cancelPending()` before the timer fires — avoiding the
  // race where `skipAll()` runs while `isPlaying === false` and the
  // setTimeout-scheduled `play(...)` fires ~600ms later during trace
  // playback. See docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  private pendingPlayTimer: ReturnType<typeof setTimeout> | null = null;

  // Manual dialogue-advance state
  /** True when we are on a dialogue step awaiting a player confirm (manual mode). */
  private awaitingDialogueConfirm = false;
  /**
   * Double-advance guard. Set to true when a confirm fires so that if the
   * input handler re-enters synchronously (e.g. two events in the same tick)
   * the second confirm is ignored until `awaitingDialogueConfirm` is re-set
   * by the NEXT dialogue step.
   */
  private dialogueConfirmConsumed = false;
  /** "Press to continue" prompt shown during manual-advance dialogue. */
  private manualAdvanceHint: TextBlock | null = null;

  // Skip input
  private skipHandler: ((evt: KeyboardEvent) => void) | null = null;
  private skipHint: TextBlock | null = null;
  private skipGamepadObserver: Observer<Scene> | null = null;
  private nextGamepadButtonDown = false;
  private skipGamepadButtonDown = false;

  // Active Elder reveal post-process. Tracked so skipStep / skipAll /
  // dispose can tear it down even if the `reveal` step is mid-flight.
  // We always dispose the *previous* instance before allocating a new
  // one, so at most one is alive at a time.
  private activeReveal: ElderRevealPostProcess | null = null;

  // Test-only hook: last-used reveal preset. Exposed so snapshot tests
  // can assert the wiring without scraping shader store internals.
  private lastRevealPreset: ElderRevealPreset | null = null;

  private unsubA11y: (() => void) | null = null;

  constructor(scene: Scene, cameraSystem: CameraSystem) {
    this.scene = scene;
    this.cameraSystem = cameraSystem;

    this.unsubA11y = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
      this.applyAccessibilityTuning();
    });
  }

  /**
   * True while a cutscene is playing. Exposed so late-attached systems
   * (notably PauseMenu, which is constructed after the opening cutscene
   * has already started and so misses the CUTSCENE_STARTED event) can
   * query current state rather than relying on an event they didn't
   * witness.
   */
  get isActive(): boolean {
    return this.isPlaying;
  }

  /** Start a cutscene with the given steps. */
  play(steps: CutsceneStep[]): void {
    this.steps = steps;
    this.currentStep = 0;
    this.runToken++;
    this.isPlaying = true;

    this.bindSkipInput();
    gameEvents.emit(GameEvents.CUTSCENE_STARTED);
    this.executeStep();
  }

  /**
   * Queue a cutscene to play after `delayMs`. Returns nothing; use
   * `cancelPending()` to abort before the timer fires.
   *
   * Used by scenes for the ~600ms intro delay so `__startAtPuzzle` can
   * cancel the pending play before it fires. Without this, the
   * `setTimeout`-scheduled intro boots ~600ms after the scene rebuild
   * and freezes the game loop (because `registerGameLoop` short-circuits
   * on `cutsceneSystem.playing`) for the entire ~16s intro.
   *
   * Only one deferred play is tracked at a time. Calling `playDeferred`
   * while another is pending clears the earlier one — intentional, since
   * scenes never queue more than one intro.
   */
  playDeferred(steps: CutsceneStep[], delayMs: number): void {
    this.cancelPending();
    this.pendingPlayTimer = setTimeout(() => {
      this.pendingPlayTimer = null;
      this.play(steps);
    }, delayMs);
  }

  /**
   * Cancel any pending `playDeferred` that hasn't fired yet. Idempotent —
   * safe to call when nothing is pending.
   */
  cancelPending(): void {
    if (this.pendingPlayTimer !== null) {
      clearTimeout(this.pendingPlayTimer);
      this.pendingPlayTimer = null;
    }
  }

  /** Skip the current step and advance to the next one. */
  skipStep(): void {
    if (!this.isPlaying) return;

    // In manual dialogue-advance mode, Space/Enter is the "advance one
    // dialogue line" confirm rather than a full step-skip. Handle it here
    // and return early so the rest of the skip path (which tears down fade
    // overlays, camera animations, etc.) does not run.
    if (this.awaitingDialogueConfirm) {
      // Double-advance guard: ignore if this confirm was already consumed
      // in the current synchronous tick.
      if (this.dialogueConfirmConsumed) return;
      this.dialogueConfirmConsumed = true;
      this.awaitingDialogueConfirm = false;
      this.hideDialogue();
      this.hideManualAdvanceHint();
      this.advance();
      return;
    }

    // Clear all pending timers (dialogue delays, wait delays, fade animations)
    for (const id of this.pendingTimers) {
      clearTimeout(id);
    }
    this.pendingTimers = [];

    // Stop any running camera animation (pan steps)
    this.scene.stopAnimation(this.cameraSystem.camera);

    // Hide dialogue if visible
    this.hideDialogue();
    this.hideManualAdvanceHint();

    // Tear down any in-flight Elder reveal so the shader + emitters
    // don't outlive the skipped step.
    if (this.activeReveal) {
      try { this.activeReveal.dispose(); } catch { /* ignore */ }
      this.activeReveal = null;
    }

    // If there's a fade overlay mid-fade, snap it to the target state
    if (this.fadeOverlay) {
      const step = this.steps[this.currentStep];
      if (step?.type === 'fade') {
        this.fadeOverlay.alpha = step.fadeDirection === 'out' ? 1 : 0;
      }
    }

    this.advance();
  }

  /** Skip the entire cutscene. */
  skipAll(): void {
    if (!this.isPlaying) return;

    // Execute any remaining callback steps (so game state stays consistent)
    for (let i = this.currentStep; i < this.steps.length; i++) {
      if (this.steps[i].type === 'callback' && this.steps[i].fn) {
        this.steps[i].fn!();
      }
    }

    this.stop();
  }

  private bindSkipInput(): void {
    this.unbindSkipInput();
    this.skipHandler = (evt: KeyboardEvent) => {
      if (!this.isPlaying) return;
      if (evt.key === ' ' || evt.key === 'Enter') {
        evt.preventDefault();
        this.skipStep();
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        this.skipAll();
      }
    };
    window.addEventListener('keydown', this.skipHandler);
    this.skipGamepadObserver = this.scene.onBeforeRenderObservable.add(() => {
      this.pollGamepadSkipInput();
    });
    this.showSkipHint();
  }

  private unbindSkipInput(): void {
    if (this.skipHandler) {
      window.removeEventListener('keydown', this.skipHandler);
      this.skipHandler = null;
    }
    if (this.skipGamepadObserver) {
      this.scene.onBeforeRenderObservable.remove(this.skipGamepadObserver);
      this.skipGamepadObserver = null;
    }
    this.nextGamepadButtonDown = false;
    this.skipGamepadButtonDown = false;
    this.hideSkipHint();
  }

  private pollGamepadSkipInput(): void {
    if (!this.isPlaying) return;
    const nextPressed = isAnyGamepadButtonPressed([0]);
    const skipPressed = isAnyGamepadButtonPressed([1, 9]);

    if (skipPressed && !this.skipGamepadButtonDown) {
      this.skipGamepadButtonDown = skipPressed;
      this.nextGamepadButtonDown = nextPressed;
      this.skipAll();
      return;
    }

    if (nextPressed && !this.nextGamepadButtonDown) {
      this.nextGamepadButtonDown = nextPressed;
      this.skipGamepadButtonDown = skipPressed;
      this.skipStep();
      return;
    }

    this.nextGamepadButtonDown = nextPressed;
    this.skipGamepadButtonDown = skipPressed;
  }

  private showSkipHint(): void {
    this.ensureGUI();
    if (!this.skipHint) {
      this.skipHint = new TextBlock(
        "skipHint",
        formatControlText("Space or Enter: next  |  Esc: skip cutscene"),
      );
      this.skipHint.color = "rgba(240, 225, 192, 0.9)";
      const scale = AccessibilitySystem.getInstance().state.fontScale;
      this.skipHint.fontSize = 18 * scale;
      this.skipHint.fontFamily = "Georgia, serif";
      // resizeToFit tells the TextBlock to size its bounding rect to
      // the text itself rather than stretching across the parent —
      // without it, the default fullwidth TextBlock would center-align
      // the text and Gemini saw "prompt in the center of the screen"
      // on L0, even though the control's horizontalAlignment was RIGHT.
      this.skipHint.resizeToFit = true;
      this.skipHint.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
      this.skipHint.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.skipHint.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.skipHint.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
      this.skipHint.left = "-24px";
      this.skipHint.shadowColor = "rgba(0,0,0,0.85)";
      this.skipHint.shadowBlur = 8;
      this.skipHint.isHitTestVisible = false;
      this.guiTexture!.addControl(this.skipHint);
    }
    this.skipHint.isVisible = true;
    this.refreshSkipHint();
    this.layoutDialogueBand();
  }

  /** Only advertise dialogue advance while a caption is actually visible. */
  private refreshSkipHint(): void {
    if (!this.skipHint) return;
    const hasDialogue = this.dialogueContainer?.isVisible || this.visionContainer?.isVisible;
    this.skipHint.text = formatControlText(hasDialogue
      ? "Space or Enter: next  |  Esc: skip cutscene"
      : "Esc: skip cutscene");
  }

  private hideSkipHint(): void {
    // Dispose rather than hide: leaving the TextBlock in the GUI tree
    // with `isVisible = false` was leaking the overlay into gameplay
    // under at least one code path (observed in the L0 QA review).
    // Re-creating it on the next cutscene is cheap.
    if (this.skipHint) {
      this.skipHint.dispose();
      this.skipHint = null;
    }
  }

  private showManualAdvanceHint(): void {
    this.ensureGUI();
    if (!this.manualAdvanceHint) {
      this.manualAdvanceHint = new TextBlock(
        "manualAdvanceHint",
        formatControlText("Space or Enter: continue"),
      );
      this.manualAdvanceHint.color = "rgba(240, 225, 192, 0.9)";
      const scale = AccessibilitySystem.getInstance().state.fontScale;
      this.manualAdvanceHint.fontSize = 16 * scale;
      this.manualAdvanceHint.fontFamily = "Georgia, serif";
      this.manualAdvanceHint.resizeToFit = true;
      this.manualAdvanceHint.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      this.manualAdvanceHint.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.manualAdvanceHint.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.manualAdvanceHint.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      this.manualAdvanceHint.shadowColor = "rgba(0,0,0,0.85)";
      this.manualAdvanceHint.shadowBlur = 8;
      this.manualAdvanceHint.isHitTestVisible = false;
      this.guiTexture!.addControl(this.manualAdvanceHint);
    }
    this.manualAdvanceHint.isVisible = true;
    this.layoutDialogueBand();
  }

  private hideManualAdvanceHint(): void {
    if (this.manualAdvanceHint) {
      this.manualAdvanceHint.isVisible = false;
    }
  }

  /** Check if a cutscene is currently playing. */
  get playing(): boolean {
    return this.isPlaying;
  }

  /** Execute the current step. */
  private executeStep(): void {
    if (this.currentStep >= this.steps.length) {
      this.finish();
      return;
    }

    const step = this.steps[this.currentStep];
    gameEvents.emit(GameEvents.CUTSCENE_STEP, { index: this.currentStep, step });

    switch (step.type) {
      case 'pan':
        this.executePan(step);
        break;
      case 'dialogue':
        this.executeDialogue(step);
        break;
      case 'wait':
        this.executeWait(step);
        break;
      case 'fade':
        this.executeFade(step);
        break;
      case 'callback':
        this.executeCallback(step);
        break;
      case 'shake':
        this.executeShake(step);
        break;
      case 'reveal':
        this.executeReveal(step);
        break;
    }
  }

  private advance(): void {
    if (!this.isPlaying) return;
    this.currentStep++;
    this.executeStep();
  }

  /** A continuation that advances only if this run is still current. */
  private next(): () => void {
    const token = this.runToken;
    return () => {
      if (token === this.runToken) this.advance();
    };
  }

  private delayedCall(ms: number, fn: () => void): void {
    const id = setTimeout(() => {
      const idx = this.pendingTimers.indexOf(id);
      if (idx >= 0) this.pendingTimers.splice(idx, 1);
      fn();
    }, ms);
    this.pendingTimers.push(id);
  }

  // ---------- Step executors ----------

  private executePan(step: CutsceneStep): void {
    const camera = this.cameraSystem.camera;
    const target = step.target ?? { x: 0, y: 2 };
    const duration = step.duration ?? 1500;

    const startPos = camera.position.clone();
    // Keep Z constant (side-scroll camera), pan X and Y
    const endPos = new Vector3(target.x, target.y, startPos.z);

    const anim = new Animation(
      "cutscenePan",
      "position",
      60,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );

    const frames = Math.round((duration / 1000) * 60);
    anim.setKeys([
      { frame: 0, value: startPos },
      { frame: frames, value: endPos },
    ]);

    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    anim.setEasingFunction(ease);

    camera.animations = [anim];
    const next = this.next();
    const token = this.runToken;
    this.scene.beginAnimation(camera, 0, frames, false, 1, () => {
      if (token !== this.runToken) return; // stopped/skipped: leave the camera alone
      // Also update the camera target to look at the new position
      camera.setTarget(new Vector3(endPos.x, endPos.y, 0));
      next();
    });
  }

  private executeDialogue(step: CutsceneStep): void {
    const duration = step.duration ?? 3000;
    const style = step.style ?? 'standard';
    this.lastDialogueStyle = style;
    this.dialoguePlacement = step.dialoguePlacement ?? 'bottom';
    if (style === 'vision') {
      this.showVisionDialogue(step.text ?? '');
    } else {
      this.showDialogue(step.speaker ?? '', step.text ?? '', step.dialoguePlacement ?? 'bottom');
    }

    const a11yState = AccessibilitySystem.getInstance().state;
    const { mode, delayMs } = resolveDialogueAdvance(a11yState, duration);

    if (mode === 'manual') {
      // Manual mode: no auto-advance timer. Wait for player confirm.
      this.awaitingDialogueConfirm = true;
      this.dialogueConfirmConsumed = false;
      this.showManualAdvanceHint();
    } else {
      // Auto mode: schedule advance after dwell-scaled delay.
      const next = this.next();
      this.delayedCall(delayMs, () => {
        this.hideDialogue();
        this.hideManualAdvanceHint();
        next();
      });
    }
  }

  private executeWait(step: CutsceneStep): void {
    const duration = step.duration ?? 1000;
    this.delayedCall(duration, this.next());
  }

  private executeFade(step: CutsceneStep): void {
    const duration = step.duration ?? 1000;
    const r = step.color?.r ?? 0;
    const g = step.color?.g ?? 0;
    const b = step.color?.b ?? 0;

    this.ensureGUI();
    if (!this.fadeOverlay) {
      this.fadeOverlay = new Rectangle("fadeOverlay");
      this.fadeOverlay.width = "100%";
      this.fadeOverlay.height = "100%";
      this.fadeOverlay.thickness = 0;
      this.fadeOverlay.isHitTestVisible = false;
      this.guiTexture!.addControl(this.fadeOverlay);
    }

    // Format color as hex string
    const hexColor = `#${this.toHex(r)}${this.toHex(g)}${this.toHex(b)}`;
    this.fadeOverlay.background = hexColor;

    if (step.fadeDirection === 'in') {
      // Fade in: overlay goes from opaque to transparent (reveal scene)
      this.fadeOverlay.alpha = 1;
      this.animateAlpha(this.fadeOverlay, 1, 0, duration, this.next());
    } else {
      // Fade out: overlay goes from transparent to opaque (hide scene)
      this.fadeOverlay.alpha = 0;
      this.animateAlpha(this.fadeOverlay, 0, 1, duration, this.next());
    }
  }

  private executeCallback(step: CutsceneStep): void {
    step.fn?.();
    this.advance();
  }

  private executeShake(step: CutsceneStep): void {
    const duration = step.duration ?? 500;
    const intensity = step.intensity ?? 0.3;

    // Delegate to CameraSystem.triggerShake so shake composes correctly
    // with the frame-rate-independent follow during live gameplay.
    // CameraSystem.triggerShake already honours reducedMotion (skips
    // shake when enabled) and applies the offset additively so it never
    // contaminates the follow-lerp target.
    this.cameraSystem.triggerShake(intensity, duration);

    // Advance the cutscene after the nominal duration so downstream
    // dialogue/pan steps keep the same pacing regardless of motion setting.
    const id = setTimeout(this.next(), duration);
    this.pendingTimers.push(id);
  }

  /**
   * Execute an Elder reveal step. Spawns a fresh
   * `ElderRevealPostProcess` keyed to the canon preset, kicks off the
   * curtain sequence, and advances the cutscene when the reveal
   * finishes. Auto-disposes the post-process on completion so the
   * shader + particle emitters don't leak into subsequent scenes.
   */
  private executeReveal(step: CutsceneStep): void {
    const duration = step.duration ?? 2500;
    const preset: ElderRevealPreset = step.preset ?? "starry-void";
    this.lastRevealPreset = preset;

    // Tear down any previous reveal that somehow survived — keeps the
    // invariant that at most one ElderRevealPostProcess is alive.
    if (this.activeReveal) {
      try { this.activeReveal.dispose(); } catch { /* ignore */ }
      this.activeReveal = null;
    }

    const reveal = new ElderRevealPostProcess(this.scene, preset);
    this.activeReveal = reveal;

    const next = this.next();
    reveal.start({
      durationMs: duration,
      onComplete: () => {
        // Dispose as soon as the curtain finishes so the shader + any
        // lingering emitters/overlays clear before the next step runs.
        if (this.activeReveal === reveal) {
          try { reveal.dispose(); } catch { /* ignore */ }
          this.activeReveal = null;
        }
        next();
      },
    });
  }

  // ---------- Dialogue UI ----------

  private ensureGUI(): void {
    if (!this.guiTexture) {
      this.guiTexture = AdvancedDynamicTexture.CreateFullscreenUI("cutsceneUI", true, this.scene);
      this.disposeGuiScaling = configureResponsiveFullscreenUI(this.guiTexture, this.scene);
    }
  }

  private showDialogue(speaker: string, text: string, placement: 'top' | 'bottom'): void {
    this.dialoguePlacement = placement;
    this.ensureGUI();

    if (!this.dialogueContainer) {
      // Container box.
      // Size + position retuned after a Gemini fluidity review flagged
      // the L3 dialogue panel as "covering the bottom third of the
      // screen, obscuring characters and hazards." Narrowed to 70%
      // width, shrunk to 132px tall, and given slightly stronger
      // background transparency so gameplay stays readable behind it.
      // Vertical offset is owned by layoutDialogueBand() (Q-033): the
      // box sits strictly above a reserved bottom hint lane so the skip
      // and manual-advance hints can never overlap the panel.
      this.dialogueContainer = new Rectangle("dialogueBox");
      this.dialogueContainer.width = "70%";
      this.dialogueContainer.adaptHeightToChildren = true;
      this.dialogueContainer.cornerRadius = 12;
      this.dialogueContainer.thickness = 2.5;
      this.dialogueContainer.color = COLORS.BRASS;
      this.dialogueContainer.background = "rgba(18, 10, 28, 0.82)";
      this.dialogueContainer.alpha = 0.95;
      this.dialogueContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.dialogueContainer.isHitTestVisible = false;

      const panel = new StackPanel("dialoguePanel");
      panel.isVertical = true;
      panel.width = 1;   // fill parent
      panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      panel.paddingLeft = "22px";
      panel.paddingTop = "18px";
      panel.paddingRight = "22px";
      panel.paddingBottom = "14px";
      this.dialogueContainer.addControl(panel);

            const scale = AccessibilitySystem.getInstance().state.fontScale;
      this.speakerTextBlock = new TextBlock("speakerName", "");
      this.speakerTextBlock.color = COLORS.BRASS;
      this.speakerTextBlock.fontSize = 22 * scale;
      this.speakerTextBlock.fontFamily = "Georgia, serif";
      this.speakerTextBlock.fontWeight = "bold";
      this.speakerTextBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      this.speakerTextBlock.height = "32px";
      this.speakerTextBlock.resizeToFit = false;
      this.speakerTextBlock.shadowColor = "rgba(0,0,0,0.85)";
      this.speakerTextBlock.shadowBlur = 6;
      panel.addControl(this.speakerTextBlock);
 
      // Message text — width must be set explicitly so textWrapping
      // knows where to break. Without it, the TextBlock inside the
      // StackPanel expands horizontally and the text never wraps.
      this.dialogueTextBlock = new TextBlock("dialogueText", "");
      this.dialogueTextBlock.color = "#f5eedb";
      this.dialogueTextBlock.fontSize = 18 * scale;
      this.dialogueTextBlock.fontFamily = "Georgia, serif";
      this.dialogueTextBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      applyWrappedTextLayout(this.dialogueTextBlock, 6);
      panel.addControl(this.dialogueTextBlock);

      this.guiTexture!.addControl(this.dialogueContainer);
    }

    this.speakerTextBlock!.text = speaker;
    this.dialogueTextBlock!.text = formatControlText(text);
    this.dialogueContainer.isVisible = true;
    this.refreshSkipHint();
    this.layoutDialogueBand();
  }

  private hideDialogue(): void {
    if (this.dialogueContainer) {
      this.dialogueContainer.isVisible = false;
    }
    if (this.visionContainer) {
      this.visionContainer.isVisible = false;
    }
    this.refreshSkipHint();
  }

  /**
   * Render a vision-style dialogue line — centered italic whisper over
   * a faint ink-bloom backdrop, no speaker pill. See SPEC-making-biscuits
   * §4 "Body" for the canonical tonal treatment.
   */
  private showVisionDialogue(text: string): void {
    this.ensureGUI();

    if (!this.visionContainer) {
      this.visionContainer = new Rectangle("visionDialogueBox");
      this.visionContainer.width = "72%";
      this.visionContainer.adaptHeightToChildren = true;
      this.visionContainer.thickness = 0;
      // Faint ink-bloom — a deep plum wash at low alpha so scene lighting
      // behind the text stays readable while the whisper hangs over it.
      this.visionContainer.background = "rgba(18, 8, 32, 0.55)";
      this.visionContainer.cornerRadius = 18;
      this.visionContainer.alpha = 0.95;
      this.visionContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      this.visionContainer.isHitTestVisible = false;

      this.visionTextBlock = new TextBlock("visionLine", "");
      // Pale bone color with heavy text shadow so the whisper reads as
      // "breath on glass" rather than a dialogue pill.
      this.visionTextBlock.color = "#f1e7d3";
      const scale = AccessibilitySystem.getInstance().state.fontScale;
      this.visionTextBlock.fontSize = 28 * scale;
      // TextBlock doesn't expose a fontStyle prop on all Babylon builds,
      // so we fold italic into the font-family string — Babylon's GUI
      // canvas renderer honors the standard CSS syntax.
      this.visionTextBlock.fontFamily = "italic Georgia, 'Times New Roman', serif";
      this.visionTextBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      applyWrappedTextLayout(this.visionTextBlock, 6);
      this.visionTextBlock.paddingTop = "26px";
      this.visionTextBlock.paddingBottom = "26px";
      this.visionTextBlock.paddingLeft = "32px";
      this.visionTextBlock.paddingRight = "32px";
      this.visionTextBlock.shadowColor = "rgba(0,0,0,0.85)";
      this.visionTextBlock.shadowBlur = 10;
      this.visionContainer.addControl(this.visionTextBlock);

      this.guiTexture!.addControl(this.visionContainer);
    }

    this.visionTextBlock!.text = text;
    this.visionContainer.isVisible = true;
    // Hide the standard dialogue box if it was showing from a prior step.
    if (this.dialogueContainer) {
      this.dialogueContainer.isVisible = false;
    }
    this.refreshSkipHint();
  }

  /**
   * Last dialogue style that was rendered. Test-only hook — exposed so
   * the vision-cutscene spec can assert that the VISION style was wired
   * through without having to scrape Babylon GUI internals.
   */
  get lastRenderedDialogueStyle(): DialogueStyle {
    return this.lastDialogueStyle;
  }

  /**
   * Last Elder reveal preset that was kicked off during a `reveal`
   * step, or `null` if no reveal has played. Test-only hook used by
   * the elder-reveal snapshot spec.
   */
  get lastRenderedRevealPreset(): ElderRevealPreset | null {
    return this.lastRevealPreset;
  }

  /**
   * Currently-active Elder reveal, if any. Exposed for tests that need
   * to verify disposal + observer cleanup without reaching through
   * private fields.
   */
  get activeElderReveal(): ElderRevealPostProcess | null {
    return this.activeReveal;
  }

  // ---------- Utilities ----------

  /** Animate a GUI control's alpha over time using requestAnimationFrame. */
  private animateAlpha(
    control: Rectangle,
    from: number,
    to: number,
    durationMs: number,
    onComplete: () => void,
  ): void {
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / durationMs, 1);
      control.alpha = from + (to - from) * t;
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        onComplete();
      }
    };
    requestAnimationFrame(tick);
  }

  private toHex(value: number): string {
    const clamped = Math.max(0, Math.min(255, Math.round(value)));
    return clamped.toString(16).padStart(2, '0');
  }

  // ---------- Lifecycle ----------

  private finish(): void {
    this.isPlaying = false;
    this.awaitingDialogueConfirm = false;
    this.dialogueConfirmConsumed = false;
    this.unbindSkipInput();
    this.hideDialogue();
    this.hideManualAdvanceHint();

    // Clean up fade overlay
    if (this.fadeOverlay) {
      this.fadeOverlay.alpha = 0;
    }

    // Kill any reveal post-process that survived a skip/stop race —
    // belt-and-braces so no shader pass leaks into the next scene.
    if (this.activeReveal) {
      try { this.activeReveal.dispose(); } catch { /* ignore */ }
      this.activeReveal = null;
    }

    gameEvents.emit(GameEvents.CUTSCENE_ENDED);
  }

  /** Force-stop the cutscene. */
  stop(): void {
    if (this.isPlaying) {
      this.runToken++;
      // Clear pending timers
      for (const id of this.pendingTimers) {
        clearTimeout(id);
      }
      this.pendingTimers = [];

      // Stop any running animations on the camera
      this.scene.stopAnimation(this.cameraSystem.camera);

      this.finish();
      return;
    }
    // Even when no cutscene is playing, ensure any stale UI left over
    // from a previous play/skipAll cycle is cleared. Gemini 3.1-pro
    // fluidity review caught the skip-hint TextBlock persisting into
    // gameplay when skipAll() raced the deferred setTimeout that
    // schedules the opening cutscene. Idempotent cleanup here closes
    // that race without affecting the happy path.
    this.unbindSkipInput();
    this.hideDialogue();
  }

  /** Dispose GUI resources when the system is no longer needed. */
  dispose(): void {
    this.unsubA11y?.();
    this.unsubA11y = null;

    // Make sure a pending deferred intro can't fire into the NEXT scene
    // after this one's been torn down by replaceScene().
    this.cancelPending();
    this.stop();
    this.disposeGuiScaling?.();
    this.disposeGuiScaling = null;
    if (this.guiTexture) {
      this.guiTexture.dispose();
      this.guiTexture = null;
    }
    this.dialogueContainer = null;
    this.speakerTextBlock = null;
    this.dialogueTextBlock = null;
    this.visionContainer = null;
    this.visionTextBlock = null;
    this.fadeOverlay = null;
    this.skipHint = null;
    this.manualAdvanceHint = null;
    this.awaitingDialogueConfirm = false;
    this.dialogueConfirmConsumed = false;
  }

  private applyAccessibilityTuning(): void {
    const state = AccessibilitySystem.getInstance().state;
    const scale = state.fontScale;
    if (this.skipHint) this.skipHint.fontSize = 18 * scale;
    if (this.manualAdvanceHint) this.manualAdvanceHint.fontSize = 16 * scale;
    if (this.speakerTextBlock) this.speakerTextBlock.fontSize = 22 * scale;
    if (this.dialogueTextBlock) this.dialogueTextBlock.fontSize = 18 * scale;
    if (this.visionTextBlock) this.visionTextBlock.fontSize = 28 * scale;

    // High-contrast: update container backgrounds and border thickness.
    const contrast = resolveContrastStyle(state.highContrastUI);
    if (this.dialogueContainer) {
      this.dialogueContainer.background = contrast.background;
      this.dialogueContainer.thickness = contrast.thickness;
    }
    if (this.visionContainer) {
      this.visionContainer.background = contrast.background;
    }

    // Font scale (and dialogue-advance mode) feed the reserved hint lane,
    // so re-run the band layout whenever accessibility state changes.
    this.layoutDialogueBand();
  }

  /**
   * Bottom-band layout (Q-033): hints live in a reserved lane at the very
   * bottom of the screen and the dialogue box sits strictly above the lane,
   * so neither the skip hint nor the manual-advance affordance can overlap
   * the dialogue panel at any fontScale (clamped 0.8-2.0).
   *   row 1 (bottom): skipHint, right-aligned
   *   row 2:          manualAdvanceHint, centered (manual mode only)
   *   above the lane: dialogueContainer (grows upward via adaptHeightToChildren)
   *
   * Deterministic and a11y-aware: computed purely from fontScale + the
   * dialogueAdvance mode. No per-frame measurement — called only from the
   * show* sites and the ACCESSIBILITY_CHANGED-driven tuning pass.
   */
  private layoutDialogueBand(): void {
    const state = AccessibilitySystem.getInstance().state;
    const hintRow = Math.ceil(18 * state.fontScale * 1.35) + 6; // line height + gap
    const laneBottom = 12;
    if (this.skipHint) this.skipHint.top = `${-laneBottom}px`;
    if (this.manualAdvanceHint) {
      this.manualAdvanceHint.top = `${-(laneBottom + hintRow)}px`;
      // Upper captions leave the cast below. The bottom hint already
      // includes advance + skip, so omit the redundant second line here.
      this.manualAdvanceHint.isVisible = state.dialogueAdvance === "manual"
        && this.awaitingDialogueConfirm && this.dialoguePlacement !== 'top';
    }
    const rows = state.dialogueAdvance === "manual" ? 2 : 1;
    if (this.dialogueContainer) {
      const upper = this.dialoguePlacement === 'top';
      this.dialogueContainer.verticalAlignment = upper
        ? Control.VERTICAL_ALIGNMENT_TOP : Control.VERTICAL_ALIGNMENT_BOTTOM;
      this.dialogueContainer.width = upper ? "82%" : "70%";
      this.dialogueContainer.top = upper ? "104px" : `${-(laneBottom + rows * hintRow + 8)}px`;
    }
  }

  // ---------- Test accessors ----------

  /**
   * Number of pending auto-advance timers. Test-only — used to assert
   * that manual mode schedules no timers.
   */
  get pendingTimerCount(): number {
    return this.pendingTimers.length;
  }

  /**
   * Index of the step currently executing. Test-only — used to assert
   * that manual mode stays on step 0 until a confirm fires.
   */
  get currentDialogueStep(): number {
    return this.currentStep;
  }

  /**
   * Background color of the dialogue container, or undefined if not yet
   * created. Test-only — used to assert high-contrast live updates.
   */
  get dialogueContainerBackground(): string | undefined {
    return this.dialogueContainer?.background ?? undefined;
  }

  /**
   * Border thickness of the dialogue container, or undefined if not yet
   * created. Test-only — used to assert high-contrast live updates.
   */
  get dialogueContainerThickness(): number | undefined {
    return this.dialogueContainer?.thickness ?? undefined;
  }

  /**
   * Background color of the vision dialogue container, or undefined if
   * not yet created. Test-only — used to assert high-contrast live updates.
   */
  get visionContainerBackground(): string | undefined {
    return this.visionContainer?.background ?? undefined;
  }

  /**
   * True when a manual-advance affordance is currently visible.
   * Test-only — used to assert that manual mode shows the prompt.
   */
  get isShowingManualAdvanceAffordance(): boolean {
    return this.manualAdvanceHint?.isVisible === true;
  }
}
